import React, { useState, useEffect } from 'react';
import { Select, Input, Space, Typography, Tag } from '@douyinfe/semi-ui';
import { IconLink } from '@douyinfe/semi-icons';

const { Text } = Typography;

// 微信小程序页面列表及说明
const MINIPROGRAM_PAGES = [
    { path: "/pages/basic/my/my", name: "个人中心", desc: "用户个人信息与设置页面", paramsRequired: false },
    { path: "/pages/basic/m/m", name: "自定义页面", desc: "自定义页面", paramsRequired: true },
    { path: "/pages/basic/editprofile/editprofile", name: "编辑资料", desc: "修改个人信息", paramsRequired: false },
    { path: "/pages/basic/tokenlist/tokenlist", name: "余额明细", desc: "查看余额流水", paramsRequired: false },
    { path: "/pages/basic/article/article", name: "文章详情", desc: "查看文章内容", paramsRequired: true },
    { path: "/pages/basic/vipcard-list/index", name: "会员卡列表", desc: "查看我的会员卡", paramsRequired: false },
    { path: "/pages/basic/balance-list/index", name: "余额明细", desc: "查看账户余额明细", paramsRequired: false },
    { path: "/pages/lesson/lessondetail/index", name: "课程详情", desc: "查看课程详细信息", paramsRequired: true },
    { path: "/pages/lesson/lessonlist/index", name: "课程列表", desc: "浏览所有课程", paramsRequired: false },
    { path: "/pages/lesson/subjectdetail/index", name: "科目详情", desc: "查看科目详细信息", paramsRequired: true },
    { path: "/pages/lesson/subjectlist/index", name: "科目列表", desc: "浏览所有科目", paramsRequired: false },
    { path: "/pages/lesson/staffdetail/index", name: "教师详情", desc: "查看教师详细信息", paramsRequired: true },
    { path: "/pages/lesson/stafflist/index", name: "教师列表", desc: "浏览所有教师", paramsRequired: false },
    { path: "/pages/lesson/orderlist/index", name: "课程订单", desc: "查看课程订单", paramsRequired: false },
    { path: "/pages/lesson/favorite/index", name: "收藏课程", desc: "查看收藏的课程", paramsRequired: false },
    { path: "/pages/lesson/history/index", name: "学习记录", desc: "查看学习历史", paramsRequired: false },
    { path: "/pages/shop/category/index", name: "商品分类", desc: "浏览商品分类目录", paramsRequired: false },
    { path: "/pages/shop/cart/index", name: "购物车", desc: "管理购物车商品", paramsRequired: false },
    { path: "/pages/shop/address/index", name: "收货地址", desc: "管理收货地址", paramsRequired: false },
    { path: "/pages/shop/coupon-center/index", name: "领券中心", desc: "浏览和领取优惠券", paramsRequired: false },
    { path: "/pages/shop/coupon/index", name: "我的优惠券", desc: "管理个人优惠券", paramsRequired: false },
    { path: "/pages/shop/orderlist/index", name: "订单列表", desc: "查看所有订单", paramsRequired: false },
    { path: "/pages/shop/orderdetail/index", name: "订单详情", desc: "查看订单详细信息", paramsRequired: true },
    { path: "/pages/shop/orderpage/index", name: "点单", desc: "快捷点单", paramsRequired: false },
    { path: "/pages/shop/productdetail/index", name: "商品详情", desc: "查看商品详细信息", paramsRequired: true },
    { path: "/pages/shop/productlist/index", name: "商品列表", desc: "浏览商品列表", paramsRequired: false }
];

/**
 * 链接选择器组件
 * @param {Object} props
 * @param {string} props.value - 当前选中的链接值
 * @param {Function} props.onChange - 值变更回调函数
 * @param {boolean} props.showParams - 是否显示参数输入框
 */
const LinkSelector = ({ value = '', onChange, showParams = true }) => {
    // 链接类型：custom(自定义) 或 miniprogram(小程序页面)
    const [linkType, setLinkType] = useState(() => {
        if (value && !MINIPROGRAM_PAGES.some(page => value.startsWith(page.path))) {
            console.log('LinkSelector: value is not a miniprogram page', value);
            return 'custom';
        }
        console.log('LinkSelector: value miniprog', value);
        return 'miniprogram';
    });

    // 选中的小程序页面
    const [selectedPage, setSelectedPage] = useState(() => {
        if (value) {
            // 从当前值中提取页面路径（不含参数）
            const pagePath = value.split('?')[0];
            return MINIPROGRAM_PAGES.find(page => pagePath === page.path)?.path || '';
        }
        return '';
    });

    // 参数字符串
    const [params, setParams] = useState(() => {
        if (linkType === 'miniprogram' && value && value.includes('?')) {
            return value.split('?')[1];
        }
        return '';
    });

    // 自定义链接
    const [customLink, setCustomLink] = useState(() => {
        return linkType === 'custom' ? value : '';
    });

    // 当外部value变化时更新内部状态
    useEffect(() => {
        if (value !== undefined) {
            const isMiniprogramPage = MINIPROGRAM_PAGES.some(page => value.startsWith(page.path));
            const newLinkType = isMiniprogramPage ? 'miniprogram' : 'custom';
            setLinkType(newLinkType);

            if (isMiniprogramPage) {
                const pagePath = value.split('?')[0];
                setSelectedPage(pagePath);
                if (value.includes('?')) {
                    setParams(value.split('?')[1]);
                } else {
                    setParams('');
                }
                setCustomLink('');
            } else {
                setSelectedPage('');
                setParams('');
                setCustomLink(value);
            }
        }
    }, [value]);

    // 处理链接类型变更
    const handleLinkTypeChange = (type) => {
        setLinkType(type);

        // 根据类型更新值
        if (type === 'custom') {
            onChange(customLink);
        } else if (type === 'miniprogram' && selectedPage) {
            const newValue = params ? `${selectedPage}?${params}` : selectedPage;
            onChange(newValue);
        } else {
            onChange('');
        }
    };

    // 处理小程序页面选择
    const handlePageChange = (page) => {
        setSelectedPage(page);
        const newValue = params ? `${page}?${params}` : page;
        onChange(newValue);
    };

    // 处理参数变更
    const handleParamsChange = (value) => {
        // 移除开头的问号（如果有）
        const cleanParams = value.startsWith('?') ? value.substring(1) : value;
        setParams(cleanParams);

        if (linkType === 'miniprogram' && selectedPage) {
            const newValue = cleanParams ? `${selectedPage}?${cleanParams}` : selectedPage;
            onChange(newValue);
        }
    };

    // 处理自定义链接变更
    const handleCustomLinkChange = (value) => {
        setCustomLink(value);
        if (linkType === 'custom') {
            onChange(value);
        }
    };

    // 将页面列表转换为扁平选项列表
    const optionList = MINIPROGRAM_PAGES.map(page => ({
        label: (
            <div style={{ padding: '8px 0' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>{page.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)', lineHeight: '1.5' }}>
                    <div>{page.desc}</div>
                    <div style={{ color: 'var(--semi-color-text-3)', marginTop: '2px' }}>{page.path}</div>
                </div>
            </div>
        ),
        value: page.path
    }));

    // 确保数据格式正确
    //console.log('LinkSelector optionList:', optionList);

    return (
        <Space vertical align="start" style={{ width: '100%' }}>
            <Select
                prefix={<IconLink />}
                style={{ width: '100%' }}
                placeholder="选择链接类型"
                value={linkType}
                onChange={handleLinkTypeChange}
            >
                <Select.Option value="custom">自定义链接</Select.Option>
                <Select.Option value="miniprogram">小程序页面</Select.Option>
            </Select>

            {linkType === 'custom' ? (
                <Input
                    placeholder="请输入链接地址"
                    value={customLink}
                    onChange={handleCustomLinkChange}
                    style={{ width: '100%' }}
                />
            ) : (
                <>
                    <Select
                        placeholder="选择小程序页面"
                        style={{ width: '100%' }}
                        value={selectedPage}
                        onChange={handlePageChange}
                        optionList={optionList}
                        showClear
                        emptyContent="暂无页面"
                        maxHeight={300}
                        virtualize={false}
                        renderSelectedItem={option => {
                            const page = MINIPROGRAM_PAGES.find(p => p.path === option.value);
                            return page ? page.name : '';
                        }}
                        defaultActiveFirstOption={false}
                        dropdownClassName="link-selector-dropdown"
                        filter={(input, option) => {
                            if (!input) return true;
                            const searchText = input.toLowerCase();
                            const labelContent = option.label.props.children;
                            const name = labelContent[0].props.children;
                            const descContent = labelContent[1].props.children;
                            const desc = descContent[0].props.children;
                            const path = descContent[1].props.children;

                            return (
                                name.toLowerCase().includes(searchText) ||
                                desc.toLowerCase().includes(searchText) ||
                                path.toLowerCase().includes(searchText)
                            );
                        }}
                    />

                    {showParams && selectedPage && (
                        <div style={{ width: '100%' }}>
                            <Text strong size="small" style={{ marginBottom: '8px', display: 'block' }}>
                                页面参数 {MINIPROGRAM_PAGES.find(page => page.path === selectedPage)?.paramsRequired ?
                                    <Tag size="small" color="red">必填</Tag> :
                                    <Tag size="small" color="blue">可选</Tag>}
                            </Text>
                            <Input
                                placeholder="例如: uid=123"
                                value={params}
                                onChange={handleParamsChange}
                                style={{ width: '100%' }}
                                prefix={params ? '?' : null}
                                validateStatus={MINIPROGRAM_PAGES.find(page => page.path === selectedPage)?.paramsRequired && !params ? 'error' : 'default'}
                            />
                        </div>
                    )}
                </>
            )}
        </Space>
    );
};

export default LinkSelector;